import { useRef, useState } from "react";
import { Play } from "lucide-react";
import { Overline, Reveal } from "@/lib/reveal";
import atLastVideo from "@/assets/at-last.mp4";
import atLastVideoWebm from "@/assets/at-last.webm";
import coverArt from "@/assets/at-last-cover.jpg";

export function AtLast() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {});
    setPlaying(true);
  };

  return (
    <section
      id="at-last"
      data-testid="at-last-section"
      aria-labelledby="at-last-heading"
      className="relative overflow-hidden py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <Reveal>
          <Overline index="03" label="A short film about the courage to begin therapy" />
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <div className="relative mx-auto max-w-4xl border border-ivory/10">
            <img
              src={coverArt}
              alt="At Last — cover art. Maya, a character in the film, stands underwater in a one-piece swimsuit, face lifted toward the turquoise light breaking the surface"
              data-testid="at-last-cover"
              className="block w-full"
            />
            <video
              ref={videoRef}
              poster={coverArt}
              controls={playing}
              playsInline
              preload="metadata"
              data-testid="at-last-video"
              aria-label="At Last — a short film about the courage to begin therapy"
              className={`absolute inset-0 h-full w-full bg-inkblack object-contain transition-opacity duration-500 ${
                playing ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
              onEnded={() => setPlaying(false)}
            >
              <source src={atLastVideoWebm} type="video/webm" />
              <source src={atLastVideo} type="video/mp4" />
            </video>

            {!playing && (
              <button
                type="button"
                data-testid="at-last-play-button"
                aria-label="Play the film At Last"
                onClick={play}
                className="group absolute inset-0 flex items-center justify-center"
              >
                <span className="flex h-20 w-20 items-center justify-center rounded-full border border-ivory/60 bg-inkblack/30 backdrop-blur-sm transition-all duration-500 group-hover:scale-105 group-hover:border-gold sm:h-24 sm:w-24">
                  <Play
                    className="h-6 w-6 fill-current text-ivory transition-colors duration-300 group-hover:text-gold"
                    aria-hidden="true"
                  />
                </span>
              </button>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.18}>
          <p className="mx-auto mt-10 max-w-xl text-center text-base leading-relaxed text-stone/80">
            Sometimes the question is not whether something has happened, but
            whether the way you have been carrying it still fits the life you
            want now.
          </p>
        </Reveal>
        <h2 id="at-last-heading" className="sr-only">
          At Last — the film
        </h2>
      </div>
    </section>
  );
}
